/**
 * A tenant was archived — by its own owner or by platform staff. The alert carries the
 * scheduled purge date, because that is the window in which the decision can still be undone.
 */

import type { PlatformEventDefinition, PlatformEventPayloadBase } from './definition'

export const PLATFORM_TENANT_ARCHIVED_EVENT_TYPE = 'platform_tenant_archived' as const

export interface PlatformTenantArchivedPayload extends PlatformEventPayloadBase {
  archivedAt: string
  scheduledPurgeAt: string
  /** True when the tenant owner archived it themselves, false when platform staff did. */
  selfService: boolean
  /** Teardown steps that failed; the archive itself still landed. */
  cleanupFailures: string[]
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string')
}

function isArchivedPayload(payload: unknown): payload is PlatformTenantArchivedPayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return (
    typeof p.tenantId === 'string' &&
    typeof p.tenantName === 'string' &&
    typeof p.archivedAt === 'string' &&
    typeof p.scheduledPurgeAt === 'string' &&
    typeof p.selfService === 'boolean' &&
    isStringArray(p.cleanupFailures)
  )
}

export const platformTenantArchivedEventDefinition: PlatformEventDefinition<PlatformTenantArchivedPayload> =
  {
    eventType: PLATFORM_TENANT_ARCHIVED_EVENT_TYPE,
    supportedChannelTypes: ['email', 'telegram'],
    validatePayload: isArchivedPayload,
    system: {
      label: 'Tenant archived',
      description:
        'A tenant was archived and is scheduled for deletion — by its own owner, or by platform staff.',
      summarize: (payload) => ({
        title: `${payload.tenantName} archived`,
        facts: [
          { label: 'Tenant', value: payload.tenantName },
          { label: 'Archived by', value: payload.selfService ? 'The tenant owner' : 'Platform staff' },
          { label: 'Data deleted on', value: payload.scheduledPurgeAt.slice(0, 10) },
          ...(payload.cleanupFailures.length > 0
            ? [{ label: 'Teardown needs attention', value: payload.cleanupFailures.join(', ') }]
            : []),
        ],
        href: '/?page=tenant-management',
      }),
    },
  }
